import React from 'react';
import {
  View,
  ScrollView,
  Pressable,
  StyleSheet,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Text } from '../../../components/ui';
import { Theme } from '../../../utils/theme';

export interface QuickAction {
  id: string;
  title: string;
  icon: keyof typeof Ionicons.glyphMap;
  userMessage: string;
  mentorResponse: string;
}

interface QuickActionPillsProps {
  onQuickAction: (action: QuickAction) => void;
  disabled?: boolean;
}

const QUICK_ACTIONS: QuickAction[] = [
  {
    id: 'book_recommendation',
    title: 'Get book recommendation',
    icon: 'book-outline',
    userMessage: 'Give me a book recommendation for today',
    mentorResponse: "I'd love to recommend the perfect book for you! To give you the most relevant suggestion, what area of your life would you like to focus on right now?\n\nAre you looking to build better habits, manage stress, improve your relationships, advance your career, or perhaps find more purpose and meaning? Maybe you want to develop leadership skills or work on something else entirely?\n\nJust tell me what's on your mind - I'll find the perfect book to help you grow in that area!"
  },
  {
    id: 'get_inspiration',
    title: 'Get inspiration',
    icon: 'bulb-outline',
    userMessage: 'I need some inspiration today',
    mentorResponse: "I'm here to help spark some motivation! What would be most inspiring for you right now?\n\nAre you working through a challenging situation and need encouragement to push forward? Maybe you're starting something new and want that extra boost of confidence? Or perhaps you've made some progress recently and want to celebrate those wins?\n\nTell me what's happening in your life - I'll help you find the inspiration that speaks directly to your heart today!"
  },
  {
    id: 'solve_challenge',
    title: 'Solve specific challenge',
    icon: 'shield-checkmark-outline',
    userMessage: 'Help me solve a specific challenge',
    mentorResponse: "I'm ready to help you work through whatever challenge you're facing. Every challenge is an opportunity to grow, and I'm here to guide you through it.\n\nWhat's the situation you're dealing with? Has this been going on for a while, or is it something new? I'd love to understand what you've already tried and what kind of outcome you're hoping for.\n\nTake your time sharing the details - the more I understand your situation, the better I can help you find a clear path forward."
  }
];

const QuickActionPills: React.FC<QuickActionPillsProps> = ({
  onQuickAction,
  disabled = false,
}) => {
  const handlePillPress = (action: QuickAction) => {
    if (!disabled) {
      onQuickAction(action);
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
      >
        {QUICK_ACTIONS.map((action, index) => (
          <Pressable
            key={action.id}
            style={({ pressed }) => [
              styles.pill,
              pressed && styles.pillPressed,
              disabled && styles.pillDisabled,
              index === 0 && styles.firstPill,
              index === QUICK_ACTIONS.length - 1 && styles.lastPill,
            ]}
            onPress={() => handlePillPress(action)}
            disabled={disabled}
          >
            <View style={styles.pillContent}>
              <Ionicons
                name={action.icon}
                size={16}
                color={disabled ? Theme.colors.textDisabled : Theme.colors.primary}
                style={styles.icon}
              />
              <Text
                variant="body2"
                style={[
                  styles.pillText,
                  disabled && styles.pillTextDisabled,
                ]}
                numberOfLines={1}
              >
                {action.title}
              </Text>
            </View>
          </Pressable>
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.colors.surface,
    borderBottomWidth: 0.5,
    borderBottomColor: Theme.colors.border,
  },
  scrollView: {
    maxHeight: 50,
  },
  scrollContent: {
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.sm,
    gap: Theme.spacing.sm,
  },
  pill: {
    backgroundColor: Theme.colors.background,
    borderRadius: Theme.borderRadius.xl,
    borderWidth: 1,
    borderColor: Theme.colors.border,
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
    minHeight: 36,
    justifyContent: 'center',
  },
  pillPressed: {
    backgroundColor: Theme.colors.primaryLight,
    borderColor: Theme.colors.primary,
    transform: [{ scale: 0.98 }],
  },
  pillDisabled: {
    backgroundColor: Theme.colors.disabled,
    borderColor: Theme.colors.border,
    opacity: 0.6,
  },
  firstPill: {
    marginLeft: 0,
  },
  lastPill: {
    marginRight: 0,
  },
  pillContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
  },
  icon: {
    marginTop: -1, // Fine-tune alignment
  },
  pillText: {
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textPrimary,
    fontSize: 14,
  },
  pillTextDisabled: {
    color: Theme.colors.textDisabled,
  },
});

export default QuickActionPills; 